import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Building2, Info, MoreVertical, Package, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { TableCell, TableRow } from "@/components/ui/table";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { toast } from "sonner";
import { formatCompactBillingPeriod } from "../format";
import { useBillingMoney, useBuildingSubscription, useRetryBuildingActivation } from "../hooks";
import { billingErrorMessage } from "../messages";
import type { BilledBuilding, BillingContext, BuildingSubscription } from "../types";
import type { Translations } from "@/lib/i18n/types";
import { fill } from "@/lib/i18n/fill";
import { BillingPlanOverview } from "./BillingPlanOverview";
import { ChangeBuildingPlanDialog } from "./ChangeBuildingPlanDialog";
import { RestrictedMenuItem } from "./RestrictedMenuItem";

interface BilledBuildingRowProps {
  building: BilledBuilding;
  currency: string;
  context: BillingContext;
  removeDisabledReason?: string;
  chooseForRemoval: (building: BilledBuilding) => void;
}

function useBuildingActivation(context: BillingContext, building: BilledBuilding) {
  const { t } = useLanguage();
  const activation = useRetryBuildingActivation();
  const retryActivation = async () => {
    try {
      const result = await activation.mutateAsync({
        orgId: context.org_id,
        buildingId: building.building_id,
      });
      if (result.activation_pending) toast.info(t.billing.plans.activationPending);
      else toast.success(t.billing.plans.activated);
    } catch (error) {
      toast.error(billingErrorMessage(error, t, t.billing.plans.changeFailed));
    }
  };
  return { pending: activation.isPending, retry: retryActivation };
}

function BuildingPlanStatus({
  subscription,
  managedBilling,
  legacyRateCents,
}: {
  subscription: BuildingSubscription;
  managedBilling?: boolean;
  legacyRateCents: number;
}) {
  const { t } = useLanguage();
  const plan =
    managedBilling === false
      ? { ...subscription.plan, rate_cents: legacyRateCents }
      : subscription.plan;
  const status = subscription.activation_pending
    ? t.billing.plans.activationPending
    : subscription.requested_tier
      ? [
          fill(t.billing.plans.pending, {
            plan: t.billing.plans.names[subscription.requested_tier],
          }),
          t.billing.plans.pendingHelp,
        ].join(". ")
      : undefined;
  return (
    <div className="flex items-center gap-1.5">
      <BillingPlanOverview plan={plan} usage={subscription.usage} />
      {status && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={status}
              className="inline-flex shrink-0 rounded-sm text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Info aria-hidden="true" className="h-3.5 w-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">{status}</TooltipContent>
          <span role="status" className="sr-only">
            {status}
          </span>
        </Tooltip>
      )}
    </div>
  );
}

function BuildingPlanCell({
  subscription,
  unavailable,
  managedBilling,
  legacyRateCents,
}: {
  subscription?: BuildingSubscription;
  unavailable: boolean;
  managedBilling?: boolean;
  legacyRateCents: number;
}) {
  const { t } = useLanguage();
  if (subscription)
    return (
      <BuildingPlanStatus
        subscription={subscription}
        managedBilling={managedBilling}
        legacyRateCents={legacyRateCents}
      />
    );
  return (
    <p className="text-xs text-muted-foreground">
      {unavailable ? t.billing.plans.unavailable : t.billing.consent.loading}
    </p>
  );
}

function ActivationActions({
  context,
  activation,
}: {
  context: BillingContext;
  activation: ReturnType<typeof useBuildingActivation>;
}) {
  const { t } = useLanguage();
  const reason = !context.is_org_owner
    ? t.billing.restrictions.ownerOnly
    : activation.pending
      ? t.billing.consent.loading
      : undefined;
  return (
    <>
      <DropdownMenuItem asChild>
        <Link to="/billing/invoices">{t.billing.banners.viewInvoice}</Link>
      </DropdownMenuItem>
      <RestrictedMenuItem onSelect={() => void activation.retry()} disabledReason={reason}>
        {t.billing.plans.retryActivation}
      </RestrictedMenuItem>
    </>
  );
}

function planChangeDisabledReason(
  context: BillingContext,
  subscription: BuildingSubscription | undefined,
  t: Translations
) {
  if (!context.is_org_owner || subscription?.can_manage === false)
    return t.billing.restrictions.ownerOnly;
  if (subscription?.requested_tier) return t.billing.plans.pendingHelp;
  if (!subscription) return t.billing.plans.unavailable;
  return undefined;
}

function BuildingPlanActions({
  building,
  context,
  subscription,
  activation,
  onChange,
}: {
  building: BilledBuilding;
  context: BillingContext;
  subscription?: BuildingSubscription;
  activation: ReturnType<typeof useBuildingActivation>;
  onChange: () => void;
}) {
  const { t } = useLanguage();
  if (subscription?.activation_pending)
    return <ActivationActions context={context} activation={activation} />;
  if (building.removal_requested_on) return null;
  return (
    <RestrictedMenuItem
      onSelect={onChange}
      disabledReason={planChangeDisabledReason(context, subscription, t)}
    >
      <Package aria-hidden="true" className="mr-2 h-4 w-4" />
      {t.billing.plans.change}
    </RestrictedMenuItem>
  );
}

function BuildingRemovalAction({
  building,
  removeDisabledReason,
  chooseForRemoval,
}: Pick<BilledBuildingRowProps, "building" | "removeDisabledReason" | "chooseForRemoval">) {
  const { t } = useLanguage();
  if (building.removal_requested_on)
    return <DropdownMenuItem disabled>{t.billing.table.removalScheduled}</DropdownMenuItem>;
  return (
    <RestrictedMenuItem
      className="text-[#ef4129] focus:text-[#ef4129]"
      disabledReason={removeDisabledReason}
      onSelect={() => chooseForRemoval(building)}
    >
      <Trash2 aria-hidden="true" className="mr-2 h-4 w-4" />
      {t.billing.table.remove}
    </RestrictedMenuItem>
  );
}

function BuildingRowMenu({
  subscription,
  activation,
  onChange,
  ...props
}: BilledBuildingRowProps & {
  subscription?: BuildingSubscription;
  activation: ReturnType<typeof useBuildingActivation>;
  onChange: () => void;
}) {
  const { building, context } = props;
  const { t } = useLanguage();
  return (
    <TableCell className="text-right">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            aria-label={`${t.billing.table.action}: ${building.building_name}`}
          >
            <MoreVertical aria-hidden="true" className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <BuildingPlanActions
            building={building}
            context={context}
            subscription={subscription}
            activation={activation}
            onChange={onChange}
          />
          {!building.removal_requested_on && <DropdownMenuSeparator />}
          <BuildingRemovalAction {...props} />
        </DropdownMenuContent>
      </DropdownMenu>
    </TableCell>
  );
}

function BuildingNameCell({ building }: Pick<BilledBuildingRowProps, "building">) {
  return (
    <TableCell>
      <div className="flex min-w-0 items-center gap-2">
        <Building2 aria-hidden="true" className="h-4 w-4 shrink-0 text-primary" />
        <p className="truncate font-medium" title={building.building_name}>
          {building.building_name}
        </p>
      </div>
    </TableCell>
  );
}

export function BilledBuildingRow(props: BilledBuildingRowProps) {
  const { building, currency, context } = props;
  const money = useBillingMoney();
  const subscription = useBuildingSubscription(building.building_id);
  const activation = useBuildingActivation(context, building);
  const [planOpen, setPlanOpen] = useState(false);
  const managedBilling =
    subscription.data?.billing_enabled ?? building.subscription_plan_billing_enabled;

  return (
    <>
      <TableRow className="border border-[#ececed] bg-white hover:bg-white [&>td:first-child]:rounded-l-md [&>td:first-child]:border-l [&>td:last-child]:rounded-r-md [&>td:last-child]:border-r lg:[&>td:last-child]:px-2 [&>td]:border-y [&>td]:border-[#ececed] [&>td]:px-2 [&>td]:py-3 lg:[&>td]:px-4">
        <BuildingNameCell building={building} />
        <TableCell>
          <BuildingPlanCell
            subscription={subscription.data}
            unavailable={subscription.isError}
            managedBilling={managedBilling}
            legacyRateCents={building.rate_cents}
          />
        </TableCell>
        <TableCell className="whitespace-nowrap text-xs lg:text-sm">
          {formatCompactBillingPeriod(building.service_start, building.service_end)}
        </TableCell>
        <TableCell className="whitespace-nowrap text-right font-medium">
          {money(building.amount_cents, currency)}
        </TableCell>
        <BuildingRowMenu
          {...props}
          subscription={subscription.data}
          activation={activation}
          onChange={() => setPlanOpen(true)}
        />
      </TableRow>
      {planOpen && subscription.data && (
        <ChangeBuildingPlanDialog
          context={context}
          subscription={{ ...subscription.data, billing_enabled: managedBilling }}
          legacyRateCents={building.rate_cents}
          name={building.building_name}
          onClose={() => setPlanOpen(false)}
        />
      )}
    </>
  );
}
